import { useEffect, useState } from "react";
import { sortAlpha } from "widgets/utils";

import { graphql } from "../api/client.ts";
import {
  extractAllProjectsFromGroups,
  extractPrivateGroups,
  getProjectsByUserGroups,
  type MinimalProject,
  type MinimalUserGroup,
} from "./projects-utils.ts";

export interface List<P> {
  name: string;
  items: P[];
}

export interface ProjectsFetchOptions<P extends MinimalProject> {
  includeStagedProjects?: boolean;
  includeAllProjects?: boolean;
  initialSelectedProjectId?: number | null;
  defaultSelection?: "private" | "previous" | "all" | null;
  onSelectionChange?: (project: P | undefined) => void;
}

export interface Result<P> {
  projectsList: P[] | null;
  projectsTree: List<P>[];
  error?: Error;
  isLoading: boolean;
  defaultSelectedProject: P | null;
}

const allProjectsItem = {
  id: -1,
  name: `All projects`,
  color: null,
  description: null,
  isPrivateWorkspace: false,
  capabilities: null,
} satisfies MinimalProject;

export const useFetchProjects = <P extends MinimalProject>({
  includeStagedProjects,
  includeAllProjects,
  defaultSelection,
}: Pick<ProjectsFetchOptions<P>, "includeAllProjects" | "includeStagedProjects" | "defaultSelection">): Result<P> => {
  const [projectsList, setProjectsList] = useState<P[] | null>(null);
  const [projectsTree, setProjectsTree] = useState<List<P>[]>([]);
  const [privateProject, setPrivateProject] = useState<P | null>(null);
  const [lastSavedTarget, setLastSavedTarget] = useState<number | null>(null);
  const [defaultSelectedProject, setDefaultSelectedProject] = useState<P | null>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error>();

  useEffect(() => {
    async function load() {
      const data = await graphql.getAccessibleProjectGroups({ includeStagedProjects });
      const groups = data?.userGroup?.accessibleUserGroups as MinimalUserGroup<P>[] | undefined;
      const allProjects: P[] | null = extractAllProjectsFromGroups(groups);

      if (includeAllProjects) {
        allProjects?.push(allProjectsItem as P);
      }

      if (groups && allProjects) {
        const _groups = getProjectsByUserGroups(groups);
        const [privateGroups, otherGroups] = extractPrivateGroups(_groups);
        const privateProject = privateGroups ? (Object.values(privateGroups)?.[0]?.[0] ?? null) : null;
        const otherItems = Object.entries(otherGroups ?? {}).map(([key, value]) => ({
          name: key,
          items: value,
        }));
        const sortedItems = otherItems.sort((a, b) => sortAlpha(a?.name ?? "", b?.name ?? ""));

        setLastSavedTarget(data.userParams?.lastSaveTarget ?? null);
        setProjectsList(allProjects);
        setPrivateProject(privateProject);
        setProjectsTree(
          privateProject || includeAllProjects
            ? [
                {
                  name: "My Places",
                  items:
                    privateProject && includeAllProjects
                      ? [allProjectsItem as P, privateProject]
                      : privateProject
                        ? [privateProject]
                        : [allProjectsItem as P],
                },
                ...sortedItems,
              ]
            : sortedItems,
        );
      }
    }

    setIsLoading(true);
    load()
      .catch(setError)
      .finally(() => setIsLoading(false));
  }, [includeAllProjects, includeStagedProjects]);

  useEffect(() => {
    if (defaultSelection === "private" && privateProject) {
      setDefaultSelectedProject(privateProject);
    } else if (defaultSelection === "previous" && lastSavedTarget && projectsList) {
      const previousProject = projectsList.find((project) => project.id === lastSavedTarget);
      if (previousProject) {
        setDefaultSelectedProject(previousProject);
      }
    } else if (defaultSelection === "all") {
      setDefaultSelectedProject(allProjectsItem as P);
    }
  }, [lastSavedTarget, privateProject, projectsList, defaultSelection]);

  return { projectsList, projectsTree, error, isLoading, defaultSelectedProject };
};
